import { createApi } from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import {
  BusinessDTO, BusinessCashRegisterDTO, BusinessCreateCashRegisterDTO, BusinessUpdateCashRegisterDTO,
  BusinessMemberDTO, BusinessMemberCreateDTO,
  BusinessMemberStatus,
  BusinessMemberUpdateDTO,
} from '../../models/business';
import baseQueryWithReauth from './base';
import UserDTO, { UserBusiness } from '../../models/user';
import InvitationDTO, { CreateInvitationDTO } from '../../models/invitation';
import { PaymentConfigDTO, PaymentProvider } from '../../models/payment-config';
import { usersApi } from './users';
// eslint-disable-next-line import/no-cycle
import { eventsApi } from './events';
import { SellerGroupDTO } from '../../models/seller_group';

const businessesApi = createApi({
  reducerPath: 'businessesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['businesses', 'invitations', 'payment-configs', 'cash-registers', 'business-members'],
  endpoints: (build) => ({
    getBusinessById: build.query<BusinessDTO | null, string>({
      providesTags: (result, error, id) => [{ type: 'businesses', id }],
      query: (id) => ({
        url: `/businesses/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BusinessDTO>): (BusinessDTO | null) => response.data || null,
    }),
    getBusinessBySubdomain: build.query<BusinessDTO | null, string>({
      query: (subdomain) => ({
        url: `/businesses/${subdomain}?nickname=true`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BusinessDTO>): (BusinessDTO | null) => response.data || null,
    }),
    getAllBusinesses: build.query<BusinessDTO[] | null, { query?: Partial<BusinessDTO> }>({
      providesTags: () => [{ type: 'businesses' }],
      query: ({ query }) => ({
        url: '/businesses',
        params: query,
        method: 'GET',
      }),
      transformResponse: (response: Response<BusinessDTO[]>): (BusinessDTO[] | null) => response.data || null,
    }),
    createBusiness: build.mutation<Response<BusinessDTO>, { business: Partial<BusinessDTO> }>({
      query: ({
        business,
      }) => ({
        url: '/businesses',
        method: 'POST',
        body: business,
      }),
      invalidatesTags: ['businesses'],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          // Invalidate both getUserBusinesses and getBusinessUsers queries
          dispatch(
            usersApi.util.invalidateTags(['user-businesses']),
          );
        } catch {
          // Handle error if needed
        }
      },
    }),
    updateBusiness: build.mutation<Response<BusinessDTO>, { business: Partial<BusinessDTO> }>({
      query: ({
        business,
      }) => ({
        url: '/businesses',
        method: 'PUT',
        body: business,
      }),
      invalidatesTags: ['businesses'],
    }),
    getBusinessUsers: build.query<UserDTO[] | null, { businessId: string }>({
      providesTags: () => [{ type: 'businesses' }],
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/users`,
        method: 'GET',
      }),
      transformResponse: (response: Response<UserDTO[]>): (UserDTO[] | null) => response.data || null,
    }),
    getBusinessInvitations: build.query<InvitationDTO[] | null, { businessId: string }>({
      providesTags: () => [{ type: 'invitations' }],
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/invitations`,
        method: 'GET',
      }),
      transformResponse: (response: Response<InvitationDTO[]>): (InvitationDTO[] | null) => response.data || null,
    }),
    createInvitation: build.mutation<Response<InvitationDTO>, { businessId: string, dto: CreateInvitationDTO }>({
      query: ({ businessId, dto }) => ({
        url: `/businesses/${businessId}/invitations`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['invitations'],
    }),
    acceptInvitation: build.mutation<Response<InvitationDTO>, { businessId: string, userId: string, token: string }>({
      query: ({
        businessId, userId, token,
      }) => ({
        url: `/businesses/${businessId}/invitations/accept`,
        method: 'POST',
        body: { user_id: userId, token },
      }),
      invalidatesTags: ['invitations'],
    }),
    rejectInvitation: build.mutation<Response<InvitationDTO>, { businessId: string, userId: string, token: string }>({
      query: ({ businessId, userId, token }) => ({
        url: `/businesses/${businessId}/invitations/reject`,
        method: 'POST',
        body: { user_id: userId, token },
      }),
    }),
    updateUserBusiness: build.mutation<Response<UserBusiness>, { businessId: string, userBusiness: Partial<UserBusiness> }>({
      query: ({ businessId, userBusiness }) => ({
        url: `/businesses/${businessId}/user-business`,
        method: 'PUT',
        body: userBusiness,
      }),
      invalidatesTags: ['businesses'],
    }),
    removeUserFromBusiness: build.mutation<Response<InvitationDTO>, { userBusinessId: string, businessId: string }>({
      query: ({ userBusinessId, businessId }) => ({
        url: `/businesses/${businessId}/user-business/${userBusinessId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['businesses'],
    }),
    deleteInvitation: build.mutation<Response<InvitationDTO>, { businessId: string, invitationId: string }>({
      query: ({ businessId, invitationId }) => ({
        url: `/businesses/${businessId}/invitations/${invitationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['invitations'],
    }),
    getEmailInvitations: build.query<InvitationDTO[] | null, { email: string }>({
      providesTags: () => [{ type: 'invitations' }],
      query: ({ email }) => ({
        url: `/invitations/email/${email}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<InvitationDTO[]>): (InvitationDTO[] | null) => response.data || null,
    }),
    authorizeMercadoPago: build.mutation<Response<{ success: boolean }>, { businessId: string, authCode: string, internal: boolean }>({
      query: ({ businessId, authCode, internal }) => ({
        url: `/businesses/${businessId}/mercadopago/authorize`,
        method: 'POST',
        body: { code: authCode, internal },
      }),
    }),
    getPaymentConfig: build.query<PaymentConfigDTO | null, { businessId: string, provider: PaymentProvider }>({
      query: ({ businessId, provider }) => ({
        url: `/businesses/${businessId}/payment-config`,
        method: 'GET',
        params: { provider, unique: true },
      }),
      providesTags: () => [{ type: 'payment-configs' }],
      transformResponse: (response: Response<PaymentConfigDTO>): (PaymentConfigDTO | null) => response.data || null,
    }),
    deletePaymentConfig: build.mutation<PaymentConfigDTO, { businessId: string, paymentConfigId: string }>({
      query: ({ businessId, paymentConfigId }) => ({
        url: `/businesses/${businessId}/payment-config/${paymentConfigId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['payment-configs'],
    }),
    updatePaymentConfig: build.mutation<PaymentConfigDTO, { businessId: string, dto: Partial<PaymentConfigDTO> }>({
      query: ({ businessId, dto }) => ({
        url: `/businesses/${businessId}/payment-config`,
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: ['payment-configs'],
    }),
    getBusinessCashRegisters: build.query<BusinessCashRegisterDTO[] | null, { businessId: string }>({
      providesTags: () => [{ type: 'cash-registers' }],
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/cash-registers`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BusinessCashRegisterDTO[]>): (BusinessCashRegisterDTO[] | null) => response.data || null,
    }),
    createBusinessCashRegister: build.mutation<Response<BusinessCashRegisterDTO>, { businessId: string, dto: BusinessCreateCashRegisterDTO }>({
      query: ({ businessId, dto }) => ({
        url: `/businesses/${businessId}/cash-registers`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['cash-registers'],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          // Invalidate both getUserBusinesses and getBusinessUsers queries
          dispatch(
            eventsApi.util.invalidateTags(['event_cash_registers']),
          );
        } catch {
          // Handle error if needed
        }
      },
    }),
    updateBusinessCashRegister: build.mutation<Response<BusinessCashRegisterDTO>, { businessId: string, dto: BusinessUpdateCashRegisterDTO }>({
      query: ({ businessId, dto }) => ({
        url: `/businesses/${businessId}/cash-registers/`,
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: ['cash-registers'],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(eventsApi.util.invalidateTags(['event_cash_registers']));
        } catch {
          // Handle error if needed
        }
      },
    }),
    deleteBusinessCashRegister: build.mutation<Response<BusinessCashRegisterDTO>, { businessId: string, cashRegisterId: string }>({
      query: ({ businessId, cashRegisterId }) => ({
        url: `/businesses/${businessId}/cash-registers/${cashRegisterId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['cash-registers'],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(eventsApi.util.invalidateTags(['event_cash_registers']));
        } catch {
          // Handle error if needed
        }
      },
    }),
    getBusinessCashRegisterById: build.query<BusinessCashRegisterDTO | null, { businessId: string, cashRegisterId: string }>({
      query: ({ businessId, cashRegisterId }) => ({
        url: `/businesses/${businessId}/cash-registers/${cashRegisterId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BusinessCashRegisterDTO>): (BusinessCashRegisterDTO | null) => response.data || null,
    }),
    getBusinessMembers: build.query<BusinessMemberDTO[] | null, { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/members`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'business-members' }],
      transformResponse: (response: Response<BusinessMemberDTO[]>): (BusinessMemberDTO[] | null) => response.data || null,
    }),
    createBusinessMember: build.mutation<Response<BusinessMemberDTO>, { businessId: string, dto: BusinessMemberCreateDTO }>({
      query: ({ businessId, dto }) => ({
        url: `/businesses/${businessId}/members`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['business-members'],
    }),
    updateBusinessMember: build.mutation<Response<BusinessMemberDTO>, { businessId: string, memberId: string, dto: BusinessMemberUpdateDTO }>({
      query: ({ businessId, memberId, dto }) => ({
        url: `/businesses/${businessId}/members/${memberId}`,
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: ['business-members'],
    }),
    destroyBusinessMember: build.mutation<Response<BusinessMemberDTO>, { businessId: string, memberId: string }>({
      query: ({ businessId, memberId }) => ({
        url: `/businesses/${businessId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['business-members'],
    }),
    updateBusinessMemberStatus: build.mutation<Response<BusinessMemberDTO>, { businessId: string, memberId: string, status: BusinessMemberStatus }>({
      query: ({ businessId, memberId, status }) => ({
        url: `/businesses/${businessId}/members/${memberId}/change-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['business-members'],
    }),
    exportMembersXlsx: build.mutation<void, { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/members/export-members-xlsx`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `lista-socios-${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}-${businessId}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    checkMemberStatus: build.mutation<Response<BusinessMemberStatus>, { businessId: string, person_id: string }>({
      query: ({ businessId, person_id }) => ({
        url: `/businesses/${businessId}/members/actions/check-status`,
        method: 'GET',
        params: { person_id },
      }),
    }),
    getSellerGroups: build.query<SellerGroupDTO[] | null, { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/businesses/${businessId}/seller-groups`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerGroupDTO[]>): (SellerGroupDTO[] | null) => response.data || null,
    }),
  }),
});

export const {
  useGetBusinessByIdQuery,
  useGetAllBusinessesQuery,
  useCreateBusinessMutation,
  useUpdateBusinessMutation,
  useGetBusinessUsersQuery,
  useGetBusinessInvitationsQuery,
  useCreateInvitationMutation,
  useGetEmailInvitationsQuery,
  useAcceptInvitationMutation,
  useRejectInvitationMutation,
  useGetBusinessBySubdomainQuery,
  useDeleteInvitationMutation,
  useRemoveUserFromBusinessMutation,
  useAuthorizeMercadoPagoMutation,
  useGetPaymentConfigQuery,
  useUpdateUserBusinessMutation,
  useDeletePaymentConfigMutation,
  useGetBusinessCashRegistersQuery,
  useCreateBusinessCashRegisterMutation,
  useUpdateBusinessCashRegisterMutation,
  useDeleteBusinessCashRegisterMutation,
  useGetBusinessCashRegisterByIdQuery,
  useGetBusinessMembersQuery,
  useCreateBusinessMemberMutation,
  useUpdateBusinessMemberMutation,
  useUpdateBusinessMemberStatusMutation,
  useExportMembersXlsxMutation,
  useLazyGetBusinessBySubdomainQuery,
  useCheckMemberStatusMutation,
  useGetSellerGroupsQuery,
  useUpdatePaymentConfigMutation,
  useDestroyBusinessMemberMutation,
} = businessesApi;
export { businessesApi };
